/**
 * Session Storage Helper
 * 
 * Provides namespaced session storage operations to prevent conflicts
 * between different pages (Image, Video, Content, etc.)
 */

const PAGE_TYPES = {
  IMAGE: 'image',
  VIDEO: 'video',
  CONTENT: 'content',
  STUDIO: 'studio'
};

/**
 * Generate a namespaced key for session storage
 * @param {string} baseKey - The base key name
 * @param {string} pageType - The page type (from PAGE_TYPES)
 * @returns {string} Namespaced key
 */
export const getSessionKey = (baseKey, pageType) => {
  return `${baseKey}_${pageType}`;
};

/**
 * Set data in session storage with namespacing
 * @param {string} key - Base key name
 * @param {any} data - Data to store
 * @param {string} pageType - Page type for namespacing
 * @returns {boolean} Success status
 */
export const setSessionData = (key, data, pageType) => {
  const namespacedKey = getSessionKey(key, pageType);
  let dataString;
  
  try {
    dataString = JSON.stringify(data);
    sessionStorage.setItem(namespacedKey, dataString);
    return true;
  } catch (error) {
    console.error(`Failed to save to sessionStorage: ${namespacedKey}`, error);
    
    // Handle quota exceeded errors
    if (error.name === 'QuotaExceededError' || error.message.includes('quota')) {
      console.warn('Session storage quota exceeded, attempting cleanup...');
      
      // Try to clear old data from other page types
      const otherPageTypes = Object.values(PAGE_TYPES).filter(type => type !== pageType);
      otherPageTypes.forEach(otherType => {
        clearPageSessionData(otherType);
      });
      
      // Retry after cleanup (only if dataString was successfully created)
      if (dataString) {
        try {
          sessionStorage.setItem(namespacedKey, dataString);
          return true;
        } catch (retryError) {
          console.error('Failed even after cleanup:', retryError);
          return false;
        }
      }
    }
    
    return false;
  }
};

/**
 * Get data from session storage with namespacing
 * @param {string} key - Base key name
 * @param {string} pageType - Page type for namespacing
 * @returns {any} Parsed data or null
 */
export const getSessionData = (key, pageType) => {
  const namespacedKey = getSessionKey(key, pageType);
  try {
    const data = sessionStorage.getItem(namespacedKey);
    return data ? JSON.parse(data) : null;
  } catch (error) {
    console.error(`Failed to get from sessionStorage: ${namespacedKey}`, error);
    return null;
  }
};

/**
 * Remove data from session storage with namespacing
 * @param {string} key - Base key name
 * @param {string} pageType - Page type for namespacing
 * @returns {boolean} Success status
 */
export const removeSessionData = (key, pageType) => {
  const namespacedKey = getSessionKey(key, pageType);
  try {
    sessionStorage.removeItem(namespacedKey);
    return true;
  } catch (error) {
    console.error(`Failed to remove from sessionStorage: ${namespacedKey}`, error);
    return false;
  }
};

/**
 * Clear all session data for a specific page type
 * @param {string} pageType - Page type to clear
 */
export const clearPageSessionData = (pageType) => {
  const keysToCheck = [
    'generatedContentData',
    'landingPageContent',
    'scheduleData',
    'publishData',
    'socialMediaFormData',
    'socialMediaFiles',
    'uploadedImageData',
    'uploadedImageMeta',
    'generatedContent'
  ];
  
  keysToCheck.forEach(baseKey => {
    removeSessionData(baseKey, pageType);
  });
};

/**
 * Migrate data from non-namespaced keys to namespaced keys
 * Useful for backward compatibility when first implementing namespacing
 * @param {string} pageType - Page type to migrate to
 */
export const migrateToNamespacedKeys = (pageType) => {
  const keysToMigrate = [
    'generatedContentData',
    'landingPageContent',
    'scheduleData',
    'publishData'
  ];
  
  keysToMigrate.forEach(baseKey => {
    try {
      const oldData = sessionStorage.getItem(baseKey);
      if (oldData) {
        const namespacedKey = getSessionKey(baseKey, pageType);
        // Only migrate if namespaced key doesn't exist
        if (!sessionStorage.getItem(namespacedKey)) {
          sessionStorage.setItem(namespacedKey, oldData);
        }
        // Remove old non-namespaced key
        sessionStorage.removeItem(baseKey);
      }
    } catch (error) {
      console.error(`Failed to migrate key ${baseKey}:`, error);
    }
  });
};

export { PAGE_TYPES };

